import { IBuildingProviderContext } from '../../@types/ContextProviderRelated';
import { EHistoryAction } from '../../hooks/useUndoRedo';
import { IWall } from '../../models/abstract/Wall';
import { IRelation } from '../../models/Relation';
import { IMultiRoomBuilding } from '../../models/standard/BuildingModel';
import CompassModel from '../../models/standard/CompassModel';
import { IElement } from '../../models/standard/ElementModel';
import { IModel2D } from '../../models/standard/Model2DModel';
import { IProduct } from '../../models/standard/ProductModel';
import { IRoom } from '../../models/standard/RoomModel';
import { NonFetchableEntity } from '../planJsonExportImportUtils';

/**
 * Converts a parsed IMultiRoomBuilding into your runtime IBuildingProviderContext.
 *
 * This version:
 *  - keeps ALL rooms
 *  - merges ALL walls (unique by UUID) from spaces + level-floating + legacy root
 *  - merges ALL products, elements, models2D from spaces + level-floating + legacy root
 *  - keeps relations + nonFetchableEntities
 *  - ensures full reversibility with exporter
 */
export const transformMultiRoomStructure = (
  building: IMultiRoomBuilding,
  compassRotation: number,
  nonFetchableEntities: Array<NonFetchableEntity>,
  relations: Array<IRelation> = [],
): IBuildingProviderContext => {
  const compass = new CompassModel(compassRotation);

  const rooms: IRoom[] = [];
  const walls: IWall[] = [];
  const products: IProduct[] = [];
  const elements: IElement[] = [];
  const models2D: IModel2D[] = [];

  const wallMap = new Map<string, IWall>();
  const productMap = new Map<string, IProduct>();
  const elementMap = new Map<string, IElement>();
  const model2DMap = new Map<string, IModel2D>();

  const bAny = building as any;

  // 1) Traverse spaces
  building.levels.forEach((level) => {
    const lvlAny = level as any;

    level.rooms.forEach((room) => {
      rooms.push(room);

      room.spaces.forEach((space) => {
        space.walls.forEach((w) => {
          if (w?.uuid && !wallMap.has(w.uuid)) wallMap.set(w.uuid, w);
        });

        space.products.forEach((p) => {
          if (p?.uuid && !productMap.has(p.uuid)) productMap.set(p.uuid, p);
        });

        space.elements.forEach((e) => {
          if (e?.uuid && !elementMap.has(e.uuid)) elementMap.set(e.uuid, e);
        });

        space.models2D.forEach((m) => {
          if (m?.uuid && !model2DMap.has(m.uuid)) model2DMap.set(m.uuid, m);
        });
      });
    });

    // 2) NEW: root-level (floating) data from each BuildingLevel
    (lvlAny.walls ?? []).forEach((w: IWall) => {
      if (w?.uuid && !wallMap.has(w.uuid)) wallMap.set(w.uuid, w);
    });

    (lvlAny.products ?? []).forEach((p: IProduct) => {
      if (p?.uuid && !productMap.has(p.uuid)) productMap.set(p.uuid, p);
    });

    (lvlAny.elements ?? []).forEach((e: IElement) => {
      if (e?.uuid && !elementMap.has(e.uuid)) elementMap.set(e.uuid, e);
    });

    (lvlAny.models2D ?? []).forEach((m: IModel2D) => {
      if (m?.uuid && !model2DMap.has(m.uuid)) model2DMap.set(m.uuid, m);
    });
  });

  // 3) Backwards compatibility: also pull in legacy root-level data from BuildingModel
  (bAny.walls ?? []).forEach((w: IWall) => {
    if (w?.uuid && !wallMap.has(w.uuid)) wallMap.set(w.uuid, w);
  });

  (bAny.products ?? []).forEach((p: IProduct) => {
    if (p?.uuid && !productMap.has(p.uuid)) productMap.set(p.uuid, p);
  });

  (bAny.elements ?? []).forEach((e: IElement) => {
    if (e?.uuid && !elementMap.has(e.uuid)) elementMap.set(e.uuid, e);
  });

  (bAny.models2D ?? []).forEach((m: IModel2D) => {
    if (m?.uuid && !model2DMap.has(m.uuid)) model2DMap.set(m.uuid, m);
  });

  walls.push(...wallMap.values());
  products.push(...productMap.values());
  elements.push(...elementMap.values());
  models2D.push(...model2DMap.values());

  return {
    compass,
    rooms,
    walls,
    products,
    elements,
    models2D,
    nonFetchableEntities,
    relations,
    isSetByUndoRedo: false,
    lastAction: EHistoryAction.BUILDING_CREATED,
  };
};
